import { useCallback, useState } from "react";

/** What a copy says when the clipboard refused it. */
export const COPY_BLOCKED = "Rhema could not reach the clipboard here, so nothing was copied.";

/** The older way to copy, for webviews and pages where the clipboard API is missing or refused. */
function copyBySelection(text: string): boolean {
  if (typeof document === "undefined" || typeof document.execCommand !== "function") return false;
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  const active = document.activeElement as HTMLElement | null;
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  active?.focus?.();
  return ok;
}

/**
 * Copies text and always answers with what to tell the person: `done` when it worked, or why
 * nothing was copied.
 */
export async function copyText(text: string, done = "Copied."): Promise<string> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return done;
    }
  } catch {
    // Refused, perhaps because the window lost focus. The older way may still work.
  }
  return copyBySelection(text) ? done : COPY_BLOCKED;
}

/**
 * Copies text with its formatting. Apps that read HTML, Rhema's message box among them, get the
 * formatting, and everything else gets the plain text. Where the clipboard takes only text, the
 * text alone is copied.
 */
export async function copyRich(text: string, html: string, done = "Copied."): Promise<string> {
  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return done;
    }
  } catch {
    // Some webviews refuse HTML on the clipboard. Text still works.
  }
  return copyText(text, done);
}

export type CopyState = "idle" | "copied" | "blocked";

/** For a Copy button: copies, then says Copied, or Could not copy with the reason. */
export function useCopy() {
  const [state, setState] = useState<CopyState>("idle");
  const copy = useCallback((text: string) => {
    void copyText(text).then((note) => setState(note === COPY_BLOCKED ? "blocked" : "copied"));
  }, []);
  const label = (idle: string) =>
    state === "copied" ? "Copied" : state === "blocked" ? "Could not copy" : idle;
  return { state, copy, label, reason: state === "blocked" ? COPY_BLOCKED : undefined };
}
